import {
  SOCIAL_PLATFORMS,
  SocialPlatform,
  SocialSentiment,
  SocialTopicAuthor,
  SocialTopicCountCell,
  SocialTopicCountsResponse,
  SocialTopicSeriesBrandRow,
  measureOf,
  netSentimentOf,
  percentOf,
  SocialFilledBucket,
  SocialSeriesCounts,
  SocialTopicMetric,
} from "@digital-twins/shared-types";

// The rules the Assistant's Topic reads share (plan D5) live in shared-types;
// re-exported so the page's imports from here keep working.
export {
  bucketStarts,
  fillBuckets,
  halfWindowDelta,
  halfWindowSplit,
  measureOf,
  MIN_DELTA_PERCENT_BASE,
  netSentimentOf,
  percentOf,
  postViewsOf,
  reachOf,
  shareOfVoice,
  shareOfVoiceRows,
  sharesLargestRemainder,
  socialTopicDelta,
} from "@digital-twins/shared-types";
export type {
  SocialFilledBucket,
  SocialHalfWindow,
  SocialSeriesBucketSize,
  SocialSeriesCounts,
  SocialSeriesRowInput,
  SocialShareOfVoice,
  SocialShareOfVoiceRow,
  SocialTopicDelta,
} from "@digital-twins/shared-types";

/**
 * Every derived number on the Topic page (plan D9). Components only render
 * what these pure functions return: no maths in JSX, so a chart and the KPI
 * strip read the same counts the same way.
 *
 * Rules held here:
 * - buckets are UTC calendar days or weeks, `YYYY-MM-DD`, gap-filled;
 * - shares of a split use largest remainder, so shown parts sum to exactly 100;
 * - a missing or zero denominator gives `null` (shown as n/a), never NaN or
 *   Infinity;
 * - rankings break ties by label, then key, so the order never flickers.
 */

function finiteCount(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return 0;
  }
  return value;
}

function compareText(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
}

export function sumBucketPosts(buckets: Array<SocialFilledBucket>): number {
  return buckets.reduce((sum, entry) => sum + entry.total.posts, 0);
}

/**
 * Channels with at least one post in the series, in `SOCIAL_PLATFORMS` order.
 * `brandedPosts` lists channels with posts naming any brand instead (share of
 * voice under a brand filter, where a channel can have branded posts but none
 * of the filtered brand).
 */
export function seriesPlatforms(
  buckets: Array<SocialFilledBucket>,
  measure: "posts" | "brandedPosts" = "posts",
): Array<SocialPlatform> {
  return SOCIAL_PLATFORMS.filter((platform) =>
    buckets.some((entry) => entry.platforms[platform][measure] > 0),
  );
}

/** Cells by count, then label, then key. A new array; the input is untouched. */
export function rankCells<T extends SocialTopicCountCell>(
  cells: Array<T>,
  labelOf: (key: string) => string = (key) => key,
): Array<T> {
  return [...cells].sort(
    (a, b) =>
      b.count - a.count ||
      compareText(labelOf(a.key), labelOf(b.key)) ||
      compareText(a.key, b.key),
  );
}

/**
 * The leading cell: the top of `rankCells` with at least one post, or null.
 * The KPI strip's leading brand and the series' share-of-voice brand both read
 * it, so they always name the same brand.
 */
export function leadingCell<T extends SocialTopicCountCell>(
  cells: Array<T>,
  labelOf?: (key: string) => string,
): T | null {
  return rankCells(cells, labelOf).find((cell) => cell.count > 0) ?? null;
}

export interface SocialTopicKpiFilters {
  platform?: SocialPlatform;
  brandId?: string;
  sentiment?: SocialSentiment;
}

export interface SocialTopicKpiInput {
  /** Posts under every filter (the counts `total`). */
  total: number;
  /** Sentiment facet: every filter except sentiment. */
  sentimentCells: Array<SocialTopicCountCell>;
  /** Channel facet: every filter except channel. */
  channelCells: Array<SocialTopicCountCell>;
  /** Brand facet: every filter except brand. */
  brandCells: Array<SocialTopicCountCell>;
  /** Posts naming any brand, under every filter except brand. `null` while unknown. */
  brandedPosts: number | null;
  filters?: SocialTopicKpiFilters;
  /** Brand names, for a stable tie break between equal brands. */
  brandLabel?: (brandId: string) => string;
}

export interface SocialTopicKpis {
  posts: number;
  netSentiment: { value: number | null; judged: number };
  topChannel: { platform: SocialPlatform | null; share: number | null };
  leadingBrand: { brandId: string | null; share: number | null };
}

function isPlatform(key: string): key is SocialPlatform {
  return (SOCIAL_PLATFORMS as ReadonlyArray<string>).includes(key);
}

/**
 * The KPI strip (plan D4). Each share excludes its own dimension, like the
 * facets: the channel and brand callouts read the filtered channel or brand
 * when one is set, the leader otherwise, and net sentiment ignores the
 * sentiment filter so it matches the trend.
 */
export function topicKpis(input: SocialTopicKpiInput): SocialTopicKpis {
  const { total, sentimentCells, channelCells, brandCells, brandedPosts, filters, brandLabel } =
    input;

  // Net sentiment leaves its own filter out, like the trend: under a
  // sentiment filter it still reads every sentiment, never a fixed +/-100.
  const sentiments = sentimentCells;
  const countOf = (key: string) =>
    sentiments.filter((cell) => cell.key === key).reduce((sum, cell) => sum + cell.count, 0);
  const judged = sentiments.reduce((sum, cell) => sum + finiteCount(cell.count), 0);

  const channels = channelCells.filter((cell) => isPlatform(cell.key));
  const channelTotal = channels.reduce((sum, cell) => sum + finiteCount(cell.count), 0);
  const channel = filters?.platform
    ? channels.find((cell) => cell.key === filters.platform) ?? {
        key: filters.platform,
        count: 0,
        small: true,
      }
    : rankCells(channels).find((cell) => cell.count > 0);

  const brand = filters?.brandId
    ? brandCells.find((cell) => cell.key === filters.brandId) ?? {
        key: filters.brandId,
        count: 0,
        small: true,
      }
    : leadingCell(brandCells, brandLabel);
  const brandShare = brand ? percentOf(brand.count, brandedPosts) : null;

  return {
    posts: finiteCount(total),
    netSentiment: {
      value: netSentimentOf(countOf("positive"), countOf("negative"), judged),
      judged,
    },
    topChannel:
      channel && channelTotal > 0
        ? { platform: channel.key as SocialPlatform, share: percentOf(channel.count, channelTotal) }
        : { platform: null, share: null },
    leadingBrand:
      brand && brandShare != null
        ? { brandId: brand.key, share: brandShare }
        : { brandId: null, share: null },
  };
}

/** The stat row's numbers from any counts under the filters; a measure an old server did not send is null. */
export interface SocialTopicStats {
  posts: number;
  reach: number | null;
  /** Posts that report views: the coverage note ("Views on 77 of 94 posts"). */
  viewsPosts: number | null;
  /** Likes, comments and shares. */
  engagement: number | null;
  creators: number | null;
}

function optionalCount(value: number | null | undefined): number | null {
  return value == null ? null : finiteCount(value);
}

export function topicStats(totals: SocialTopicCountsResponse | null): SocialTopicStats | null {
  if (!totals) {
    return null;
  }
  return {
    posts: finiteCount(totals.total),
    reach: optionalCount(totals.views),
    viewsPosts: optionalCount(totals.viewsPosts),
    engagement: optionalCount(totals.engagement),
    creators: optionalCount(totals.creators),
  };
}

/** One cubic segment of a monotone curve: `from`, two control points, `to`. */
export interface SocialCurveSegment {
  from: [number, number];
  c1: [number, number];
  c2: [number, number];
  to: [number, number];
}

/**
 * Monotone cubic (Fritsch-Carlson) segments through `points`, sorted by x:
 * each segment stays between its two end values, so a curve never dips below
 * zero or above a peak it does not have.
 */
export function monotoneSegments(points: Array<[number, number]>): Array<SocialCurveSegment> {
  const n = points.length;
  if (n < 2) {
    return [];
  }
  const dx: Array<number> = [];
  const slopes: Array<number> = [];
  for (let i = 0; i < n - 1; i += 1) {
    dx[i] = points[i + 1][0] - points[i][0];
    slopes[i] = dx[i] === 0 ? 0 : (points[i + 1][1] - points[i][1]) / dx[i];
  }
  const tangents: Array<number> = new Array(n).fill(0);
  tangents[0] = slopes[0];
  tangents[n - 1] = slopes[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    tangents[i] = slopes[i - 1] * slopes[i] <= 0 ? 0 : (slopes[i - 1] + slopes[i]) / 2;
  }
  for (let i = 0; i < n - 1; i += 1) {
    if (slopes[i] === 0) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
      continue;
    }
    const a = tangents[i] / slopes[i];
    const b = tangents[i + 1] / slopes[i];
    const sum = a * a + b * b;
    if (sum > 9) {
      const k = 3 / Math.sqrt(sum);
      tangents[i] = k * a * slopes[i];
      tangents[i + 1] = k * b * slopes[i];
    }
  }
  const segments: Array<SocialCurveSegment> = [];
  for (let i = 0; i < n - 1; i += 1) {
    const h = dx[i] / 3;
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    segments.push({
      from: [x0, y0],
      c1: [x0 + h, y0 + tangents[i] * h],
      c2: [x1 - h, y1 - tangents[i + 1] * h],
      to: [x1, y1],
    });
  }
  return segments;
}

function pointText(point: [number, number]): string {
  return `${point[0].toFixed(1)},${point[1].toFixed(1)}`;
}

/** An SVG path `d` of the monotone curve through `points`; "" for none. */
export function monotonePath(points: Array<[number, number]>): string {
  if (points.length === 0) {
    return "";
  }
  let path = `M${pointText(points[0])}`;
  for (const segment of monotoneSegments(points)) {
    path += ` C${pointText(segment.c1)} ${pointText(segment.c2)} ${pointText(segment.to)}`;
  }
  return path;
}

export interface SocialStackPoint {
  start: string;
  y0: number;
  y1: number;
  /** The channel's value that bucket in the measure. */
  value: number;
}

export interface SocialStackLayer {
  platform: SocialPlatform;
  points: Array<SocialStackPoint>;
}

export interface SocialStackLayers {
  /** Channels with a value in the measure, largest total first (ties in SOCIAL_PLATFORMS order); bottom up. */
  order: Array<SocialPlatform>;
  layers: Array<SocialStackLayer>;
  /** Per bucket, the sum of every layer: what the total line traces. */
  totals: Array<number>;
}

function bucketMeasure(counts: SocialSeriesCounts, metric: SocialTopicMetric): number {
  return measureOf(metric, counts.posts, counts.views);
}

/** Stacked channel areas of the reach (or posts) timeline. */
export function stackLayers(
  buckets: Array<SocialFilledBucket>,
  metric: SocialTopicMetric,
): SocialStackLayers {
  const totalsByPlatform = SOCIAL_PLATFORMS.map((platform, index) => ({
    platform,
    index,
    total: buckets.reduce((sum, entry) => sum + bucketMeasure(entry.platforms[platform], metric), 0),
  }));
  const order = totalsByPlatform
    .filter((entry) => entry.total > 0)
    .sort((a, b) => b.total - a.total || a.index - b.index)
    .map((entry) => entry.platform);
  const running = buckets.map(() => 0);
  const layers = order.map((platform) => ({
    platform,
    points: buckets.map((entry, bucketIndex) => {
      const value = bucketMeasure(entry.platforms[platform], metric);
      const y0 = running[bucketIndex];
      running[bucketIndex] = y0 + value;
      return { start: entry.start, y0, y1: y0 + value, value };
    }),
  }));
  return { order, layers, totals: running };
}

/** Net sentiment per bucket, -100 to 100; null for a bucket with no judged post. */
export function dailyNet(buckets: Array<SocialFilledBucket>): Array<number | null> {
  return buckets.map((entry) => {
    const { positive, negative, neutral, mixed } = entry.total;
    return netSentimentOf(positive, negative, positive + negative + neutral + mixed);
  });
}

/** Smallest value a peak can have: 1 view, or 3 posts (a 2-post day is never a story). */
const PEAK_MIN_VALUE: Record<SocialTopicMetric, number> = { reach: 1, posts: 3 };
const PEAK_SD_FACTOR = 1.2;
const PEAK_LETTERS = ["A", "B"] as const;

export interface SocialTopicPeak {
  index: number;
  value: number;
  letter: (typeof PEAK_LETTERS)[number];
  /** `value` over the mean of `values`; null when the mean is 0. */
  multipleOfAverage: number | null;
}

/**
 * Peaks of the total line: local maxima at or above mean + 1.2 sd (and the
 * measure's minimum), the 2 largest, lettered in day order. A plateau peaks
 * once, at its last bucket; a flat series has none.
 */
export function findPeaks(values: Array<number>, metric: SocialTopicMetric): Array<SocialTopicPeak> {
  const clean = values.map((value) => finiteCount(value));
  const n = clean.length;
  if (n === 0) {
    return [];
  }
  const mean = clean.reduce((sum, value) => sum + value, 0) / n;
  const sd = Math.sqrt(clean.reduce((sum, value) => sum + (value - mean) ** 2, 0) / n);
  if (sd === 0) {
    return [];
  }
  const threshold = Math.max(PEAK_MIN_VALUE[metric], mean + PEAK_SD_FACTOR * sd);
  const candidates = clean
    .map((value, index) => ({ value, index }))
    .filter(({ value, index }) => {
      const previous = index > 0 ? clean[index - 1] : Number.NEGATIVE_INFINITY;
      const next = index < n - 1 ? clean[index + 1] : Number.NEGATIVE_INFINITY;
      return value >= threshold && value >= previous && value > next;
    })
    .sort((a, b) => b.value - a.value || a.index - b.index)
    .slice(0, PEAK_LETTERS.length)
    .sort((a, b) => a.index - b.index);
  return candidates.map((candidate, rank) => ({
    index: candidate.index,
    value: candidate.value,
    letter: PEAK_LETTERS[rank],
    multipleOfAverage: mean > 0 ? candidate.value / mean : null,
  }));
}

export interface SocialTopVoice {
  author: SocialTopicAuthor;
  /** Views in reach mode, posts in posts mode. */
  primary: number;
  /** The other measure. */
  secondary: number;
}

/** Top voices: by reach then posts, or posts then reach, ties by platform then handle. */
export function topVoices(
  authors: Array<SocialTopicAuthor>,
  metric: SocialTopicMetric,
  limit = 5,
): Array<SocialTopVoice> {
  return authors
    .map((author) => ({
      author,
      primary: measureOf(metric, author.posts, author.views),
      secondary: measureOf(metric === "reach" ? "posts" : "reach", author.posts, author.views),
    }))
    .sort(
      (a, b) =>
        b.primary - a.primary ||
        b.secondary - a.secondary ||
        compareText(a.author.platform, b.author.platform) ||
        compareText(a.author.handle.toLowerCase(), b.author.handle.toLowerCase()),
    )
    .slice(0, Math.max(0, limit));
}

/** One brand's line across the chart's days, in the measure. */
export interface SocialBrandLine {
  brandId: string;
  /** One value per day of `days`, 0 where the brand has no row. */
  values: Array<number>;
  total: number;
}

/**
 * Brand lines of the timeline (`split=brand` rows) across `days`, in the
 * measure: brands of `brandIds` with something in the window, largest total
 * first, ties in `brandIds` order. Lines, never stacked: a post naming two
 * brands is in both.
 */
export function brandLines(
  rows: Array<SocialTopicSeriesBrandRow>,
  days: Array<string>,
  metric: SocialTopicMetric,
  brandIds: Array<string>,
): Array<SocialBrandLine> {
  const dayIndex = new Map(days.map((day, index) => [day, index]));
  const lines = new Map(
    brandIds.map((brandId) => [brandId, days.map(() => 0)] as [string, Array<number>]),
  );
  for (const row of rows) {
    const values = lines.get(row.brandId);
    const index = dayIndex.get(row.start);
    if (values && index != null) {
      values[index] += measureOf(metric, row.posts, row.views);
    }
  }
  return brandIds
    .map((brandId) => {
      const values = lines.get(brandId) ?? [];
      return { brandId, values, total: values.reduce((sum, value) => sum + value, 0) };
    })
    .filter((line) => line.total > 0)
    .map((line, order) => ({ line, order }))
    .sort((a, b) => b.line.total - a.line.total || a.order - b.order)
    .map((entry) => entry.line);
}
